import React, { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makePromiseCancelable } from 'client/utils/promise';
import { useResponseStream } from 'site-modules/canvas/hooks/use-response-stream';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { CANVAS_CREATIVE_ID, ERROR_CREATIVE_ID, SELECTION_TYPE } from 'site-modules/canvas/constants/canvas';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';

import './suggested-messages.scss';

export function SuggestedMessages({ message, onSubmit, inputRef = null }) {
  const { responsePromise } = message;
  const [responseId, setResponseId] = useState(null);
  const [isStreamCompleted, setStreamCompleted] = useState(false);
  const [suggestedMessages, setSuggestedMessages] = useState(null);
  const containerRef = useRef();
  const hasSuggestedMessages = !!suggestedMessages?.length;
  const [isIntersecting, disconnect] = useOnScreen(containerRef, { rootMargin: '-120px' }, true, hasSuggestedMessages);
  const handleStreamComplete = useCallback(() => setStreamCompleted(true), []);

  useEffect(() => {
    if (isIntersecting && hasSuggestedMessages) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
        event_data: {
          action_name: TrackingConstant.ACTION_WIDGET_VIEW,
          action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
          total_page_height: Math.max(document.body.scrollHeight, document.body.offsetHeight),
          creative_id: 'suggested-messages',
        },
      });
      disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSuggestedMessages, isIntersecting]);

  const [responseText] = useResponseStream(responseId, {
    onComplete: handleStreamComplete,
    creativeId: ERROR_CREATIVE_ID.SUGGESTED,
  });

  useEffect(() => {
    if (!responsePromise) {
      return undefined;
    }

    const { promise, cancel } = makePromiseCancelable(responsePromise);

    promise
      .then(id => setResponseId(id))
      .catch(error => {
        if (!error?.isCanceled) {
          setResponseId(null);
        }
      });

    return cancel;
  }, [responsePromise]);

  useEffect(() => {
    if (!isStreamCompleted || inputRef?.current?.value?.length) {
      return;
    }

    try {
      const responseSuggestedMessages = JSON.parse(responseText)?.follow_ups;
      startTransition(() => setSuggestedMessages(responseSuggestedMessages?.length ? responseSuggestedMessages : null));
    } catch {
      startTransition(() => setSuggestedMessages(null));
    }
  }, [isStreamCompleted, responseText, inputRef]);

  const onMessageClick = useCallback(
    event => {
      onSubmit(event?.target?.innerText, { selectionType: SELECTION_TYPE.SUGGESTED_MESSAGE });
    },
    [onSubmit]
  );

  useEffect(() => {
    if (hasSuggestedMessages) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          subaction_name: 'show_suggested_message',
          creative_id: CANVAS_CREATIVE_ID,
        },
      });
    }
  }, [hasSuggestedMessages]);

  if (!hasSuggestedMessages) {
    return null;
  }

  return (
    <ul ref={containerRef} className="suggested-messages list-unstyled w-100 d-flex flex-column gap-0_5 mb-0_75 medium">
      {suggestedMessages.map(suggestion => (
        <li key={suggestion} className="d-flex gap-0_75">
          <button
            className="text-transform-none fw-normal text-start list-btn border-0 bg-transparent px-0 py-0"
            onClick={onMessageClick}
          >
            {suggestion}
          </button>
        </li>
      ))}
    </ul>
  );
}

SuggestedMessages.propTypes = {
  message: PropTypes.shape({
    responsePromise: PropTypes.instanceOf(Promise),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  inputRef: PropTypes.shape({
    current: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
};
